import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useFinance } from "@/context/FinanceContext";
import { useToast } from "@/hooks/use-toast";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { DateInput } from "@/components/ui/date-input";

const schema = z
  .object({
    originAccountId: z.string().min(1, "Selecione a conta de origem"),
    destinationAccountId: z.string().min(1, "Selecione a conta de destino"),
    value: z.coerce.number().positive("Informe um valor válido"),
    
    issueDate: z.string().optional(),
    paymentDate: z.string().optional(),
    settled: z.boolean().optional(),
  })
  .refine((data) => data.originAccountId !== data.destinationAccountId, {
    message: "Contas de origem e destino devem ser diferentes",
    path: ["destinationAccountId"],
  });

export type TransferEditValues = z.infer<typeof schema>;

export function TransferEditForm({ groupId, onSubmitted }: { groupId: string; onSubmitted?: () => void }) {
  const { transactions, accounts, updateTransaction } = useFinance();
  const { toast } = useToast();

  const originTx = React.useMemo(() => transactions.find((t) => t.transferGroupId === groupId && t.type === "despesa"), [transactions, groupId]);
  const destTx = React.useMemo(() => transactions.find((t) => t.transferGroupId === groupId && t.type === "receita"), [transactions, groupId]);

  const form = useForm<TransferEditValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      originAccountId: originTx?.accountId || "",
      destinationAccountId: destTx?.accountId || "",
      value: originTx?.value ?? destTx?.value ?? (undefined as any),
      
      issueDate: originTx?.issueDate || "",
      paymentDate: originTx?.paymentDate || "",
      settled: originTx?.settled ?? true,
    },
  });

  if (!originTx || !destTx) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">Não foi possível localizar os lançamentos desta transferência.</p>
      </div>
    );
  }

  // Verificar se a transferência está conciliada (bloqueia edição)
  if (originTx.reconciled === true || destTx.reconciled === true) {
    return (
      <div className="space-y-4">
        <div className="p-4 bg-muted/50 rounded-lg border">
          <p className="text-sm font-medium text-destructive">Edição não permitida</p>
          <p className="text-sm text-muted-foreground mt-1">
            Não é possível editar transferências conciliadas. Desfaça a conciliação na aba de Conciliação antes de editar.
          </p>
        </div>
      </div>
    );
  }

  const onSubmit = async (values: TransferEditValues) => {
    const originName = accounts.find((a) => a.id === values.originAccountId)?.name ?? "origem";
    const destName = accounts.find((a) => a.id === values.destinationAccountId)?.name ?? "destino";

    try {
      await updateTransaction(originTx.id, {
        accountId: values.originAccountId,
        value: values.value,
        issueDate: values.issueDate || undefined,
        paymentDate: values.paymentDate || undefined,
        description: `Transferência para ${destName}`,
        parcelMode: undefined,
        parcels: null,
        settled: values.settled ?? true,
      });

      await updateTransaction(destTx.id, {
        accountId: values.destinationAccountId,
        value: values.value,
        
        issueDate: values.issueDate || undefined,
        paymentDate: values.paymentDate || undefined,
        description: `Transferência de ${originName}`,
        parcelMode: undefined,
        parcels: null,
        settled: values.settled ?? true,
      });

      toast({ title: "✓ Transferência atualizada", description: "Alterações salvas com sucesso!", duration: 2000 });
      onSubmitted?.();
    } catch (error: any) {
      console.error('❌ Erro ao atualizar transferência:', error?.message);
      toast({
        title: "Erro ao atualizar", 
        description: "Não foi possível salvar as alterações. Tente novamente.",
        variant: "destructive",
        duration: 3000
      });
    }
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-2">
        <Label className="text-sm font-normal">Origem do Lançamento</Label>
        <RadioGroup value="transfer" className="inline-flex items-center gap-6 w-full">
          <div className="flex items-center space-x-2">
            <RadioGroupItem id="origem-transfer" value="transfer" disabled />
            <Label htmlFor="origem-transfer" className="text-sm font-normal">Transferência</Label>
          </div>
        </RadioGroup>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm font-normal">Conta de origem</Label>
          <Select onValueChange={(v) => form.setValue("originAccountId", v)} defaultValue={form.getValues("originAccountId") || undefined}>
            <SelectTrigger aria-invalid={!!form.formState.errors.originAccountId} className={cn(form.formState.errors.originAccountId && "border-destructive focus-visible:ring-destructive")}>
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent className="z-50 bg-background text-foreground">
              {accounts.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-normal">Conta de destino</Label>
          <Select onValueChange={(v) => form.setValue("destinationAccountId", v)} defaultValue={form.getValues("destinationAccountId") || undefined}>
            <SelectTrigger aria-invalid={!!form.formState.errors.destinationAccountId} className={cn(form.formState.errors.destinationAccountId && "border-destructive focus-visible:ring-destructive")}>
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent className="z-50 bg-background text-foreground">
              {accounts.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-normal">Valor</Label>
          <Input
            type="number"
            step="0.01"
            {...form.register("value")}
            aria-invalid={!!form.formState.errors.value}
            className={cn(form.formState.errors.value && "border-destructive focus-visible:ring-destructive")}
          />
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-normal">Emissão</Label>
          <DateInput
            value={form.watch("issueDate")}
            onChange={(value) => form.setValue("issueDate", value)}
            className="w-full"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-normal">Status</Label>
          <Select onValueChange={(v) => form.setValue("settled", v === "realizado")} defaultValue={form.getValues("settled") ? "realizado" : "pendente"}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione o status" />
            </SelectTrigger>
            <SelectContent className="z-50 bg-background text-foreground">
              <SelectItem value="realizado">Realizado</SelectItem>
              <SelectItem value="pendente">Pendente</SelectItem>
            </SelectContent>
          </Select>
        </div>


        <div className="space-y-2">
          <Label className="text-sm font-normal">Data de Pagamento</Label>
          <DateInput
            value={form.watch("paymentDate")}
            onChange={(value) => form.setValue("paymentDate", value)}
            className="w-full"
          />
        </div>
      </div>

      <div className="pt-2 flex justify-end gap-2">
        <Button type="submit" variant="default">Salvar</Button>
      </div>
    </form>
  );
}
